import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Maybe } from '@graphql-tools/utils'
import { useMomentStory } from '@/contexts/MomentStoryContext'
import { MomentStoryPanel } from '@/organisms/CollectibleSceneInfo/EngagementDetailOverlay/MomentStoryPanel'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { EngagementDetail } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { PlayableStory } from '@/utils/StoryUtil/StoryUtil'

interface EngagementDetailOverlayContextState {
  isOpen: boolean
  onOpen: (story: PlayableStory) => void
  onClose: () => void
  hideMomentFullscreen: boolean
}

const noop = () => undefined

const EngagementDetailOverlayContext = React.createContext<EngagementDetailOverlayContextState>({
  isOpen: false,
  onOpen: noop,
  onClose: noop,
  hideMomentFullscreen: false,
})

interface EngagementDetailOverlayProps {
  children: React.ReactElement
  defaultEngagementDetail: Maybe<EngagementDetail>
  hideMomentFullscreen?: boolean
}

export const EngagementDetailOverlay: React.FC<EngagementDetailOverlayProps> = ({
  children,
  hideMomentFullscreen = false,
}) => {
  const { selectUserStory, clearSelectedUserStory } = useMomentStory()
  const [isOpen, setIsOpen] = useState(false)
  const { isEngagementPanelOpen } = useCollectiblesPlayerEngagement()

  const onClose = useCallback(() => {
    setIsOpen(false)
    clearSelectedUserStory()
    const html = document.getElementsByTagName('html')[0]
    html.style.height = 'auto'
    html.style.overflow = 'auto'
  }, [clearSelectedUserStory])

  const onOpen = useCallback(
    (story: PlayableStory) => {
      setIsOpen(true)
      selectUserStory(story)
      const html = document.getElementsByTagName('html')[0]
      html.style.height = '100vh'
      html.style.overflow = 'hidden'
    },
    [selectUserStory],
  )

  useEffect(() => {
    if (!isEngagementPanelOpen) {
      onClose()
    }
  }, [isEngagementPanelOpen, onClose])

  const value = useMemo(() => {
    return {
      onOpen,
      onClose,
      isOpen,
      hideMomentFullscreen,
    }
  }, [onOpen, onClose, isOpen, hideMomentFullscreen])

  return (
    <EngagementDetailOverlayContext.Provider value={value}>
      {children}
      <MomentStoryPanel onClose={onClose} isOpen={isOpen} />
    </EngagementDetailOverlayContext.Provider>
  )
}

export const useEngagementDetailOverlay = () => {
  return React.useContext(EngagementDetailOverlayContext)
}
